import AppShell from '../../components/AppShell';
import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { LucideFunnelPlus } from 'lucide-react';
import countryData from '../../data/countryData.json';

const API = process.env.REACT_APP_API_BASE;

const interests = ['Customer', 'Partner', 'Investor', 'Reseller', 'Job Seeker'];
const predefinedCountries = ['United Arab Emirates', 'India', 'Other'];

export default function LeadCollectorApp() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    isd: '',
    company: '',
    notes: '',
    country: 'United Arab Emirates',
    customCountry: '',
    interest: '',
    file: null,
    filePreview: ''
  });
  const [loading, setLoading] = useState(false);
  const [extracting, setExtracting] = useState(false);

  const getISDByCountry = (countryName) => {
    const found = countryData.find(c => c.name === countryName);
    return found ? found.isd : '';
  };
  useEffect(() => {
    document.title = "Lead Collector – Ennoverse";
  }, []);

  useEffect(() => {
    if (form.country === 'Other') {
      const isd = getISDByCountry(form.customCountry);
      if (isd) setForm(prev => ({ ...prev, isd }));
    } else {
      const isd = getISDByCountry(form.country);
      if (isd) setForm(prev => ({ ...prev, isd }));
    }
  }, [form.country, form.customCountry]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'file') {
      const file = files[0];
      if (!file) return;
      const uploadData = new FormData();
      uploadData.append('file', file);

      const fileUrl = URL.createObjectURL(file);
      setForm(prev => ({ ...prev, file, filePreview: fileUrl }));
      setExtracting(true);

      fetch(`${API}/api/leads/upload-card`, {
        method: 'POST',
        body: uploadData,
      })
        .then(res => res.json())
        .then(data => {
          setForm(prev => ({
            ...prev,
            name: data.name || '',
            email: data.email || '',
            phone: data.phone || '',
            company: data.company || ''
          }));
        })
        .catch(() => {
          toast.error('Failed to extract business card');
        })
        .finally(() => {
          setExtracting(false);
        });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleSubmit = async () => {
    if (!form.name || !form.phone || !form.email) {
      toast.error('Name, email, and phone are required');
      return;
    }

    setLoading(true);
    const payload = new FormData();
    Object.keys(form).forEach((key) => {
      if (form[key] && key !== 'customCountry' && key !== 'filePreview') {
        payload.append(key, form[key]);
      }
    });
    if (form.country === 'Other') {
      payload.append('country', form.customCountry);
    }
    payload.append('tag', 'aiexpo-2025');

    try {
      const res = await fetch(`${API}/api/leads`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: payload,
      });

      if (res.ok) {
        toast.success('Lead captured');
      } else {
        toast.error('Failed to save lead');
      }
    } catch (err) {
      toast.error('Server error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppShell>
      
      <div className="col-span-12 relative">
        {extracting && (
          <div className="fixed inset-0 z-50 bg-white bg-opacity-75 flex items-center justify-center">
            <div className="text-center space-y-2">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500 mx-auto" />
              <p className="text-blue-700 text-sm">Extracting details from card...</p>
            </div>
          </div>
        )}
        <div className="text-center space-y-1">
          <h1 className="text-2xl font-semibold flex items-center justify-center gap-2">
            <LucideFunnelPlus className="w-5 h-5 text-primary" /> Capture Leads
          </h1>
          <p className="text-gray-500 text-sm">
            Capture high-quality leads in seconds with name, phone, notes, and business card uploads — built for on-the-go expo teams.
          </p>
        </div>

        <div className="max-w-md mx-auto p-4 space-y-4">
          <label className="text-sm text-muted-foreground">Business Card (photo or PDF)</label>
          <Input type="file" name="file" accept="image/*,.pdf" onChange={handleChange} />

          {form.filePreview && (
            <img src={form.filePreview} alt="Card Preview" className="w-full max-h-64 object-contain border rounded" />
          )}

          <Input name="name" value={form.name} onChange={handleChange} placeholder="Full Name *" />

          <Input name="email" value={form.email} onChange={handleChange} placeholder="Email *" />

          <div className="space-y-1">
            <label className="text-sm font-medium">Country</label>
            <div className="flex gap-2 flex-wrap">
              {predefinedCountries.map((c) => (
                <button
                  key={c}
                  onClick={() => setForm({ ...form, country: c, customCountry: '' })}
                  className={`px-3 py-1 rounded border text-sm ${form.country === c ? 'bg-blue-100 border-blue-500' : 'border-gray-300'}`}
                >
                  {c}
                </button>
              ))}
            </div>
            {form.country === 'Other' && (
              <select
                name="customCountry"
                value={form.customCountry}
                onChange={handleChange}
                className="w-full border px-3 py-2 rounded mt-2"
              >
                <option value="">Select Country</option>
                {countryData.map((c) => (
                  <option key={c.name} value={c.name}>{c.name}</option>
                ))}
              </select>
            )}
          </div>

          <div className="flex gap-2">
            <select name="isd" value={form.isd} onChange={handleChange} className="w-1/3 p-2 border rounded">
              {countryData.map((c) => (
                <option key={c.name} value={c.isd}>{c.isd}</option>
              ))}
            </select>
            <Input name="phone" value={form.phone} onChange={handleChange} placeholder="Phone Number *" className="w-2/3" />
          </div>

          <Input name="company" value={form.company} onChange={handleChange} placeholder="Company (optional)" />

          <div className="space-y-1">
            <label className="text-sm font-medium">Interested As</label>
            <div className="flex gap-2 flex-wrap">
              {interests.map((type) => (
                <button
                  key={type}
                  onClick={() => setForm({ ...form, interest: type })}
                  className={`px-3 py-1 rounded border text-sm ${form.interest === type ? 'bg-green-100 border-green-500' : 'border-gray-300'}`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          <Textarea name="notes" value={form.notes} onChange={handleChange} placeholder="Notes (optional)" rows={3} />
          <Button onClick={handleSubmit} disabled={loading} className="w-full">
            {loading ? 'Saving...' : 'Save Lead'}
          </Button>
        </div>
      </div>
      </AppShell>
  );
}
